import posthog from "posthog-js";
import { isLocalDev } from "@/lib/posthog";
import { sanitizePath } from "./sanitize-url";

/**
 * Shared PostHog capture helper.
 * All feature tracking files import from here.
 *
 * Automatically injects common properties (tenant, page) into every event.
 * On localhost, logs events to the console instead of sending to PostHog.
 * Disabled during user impersonation to avoid polluting analytics.
 *
 * Dependencies are lazily imported to avoid breakage when tests mock
 * @/services/auth or @/helpers/tenant without including all exports.
 */
export const capture = (name: string, properties?: Record<string, unknown>) => {
  try {
    const { isImpersonating } = require("@/services/auth");
    if (typeof isImpersonating === "function" && isImpersonating()) {
      return;
    }
  } catch {
    // auth module unavailable — proceed with capture
  }

  let tenant: string | undefined;
  try {
    const { getTenant } = require("@/helpers/tenant");
    if (typeof getTenant === "function") {
      tenant = getTenant();
    }
  } catch {
    // tenant module unavailable
  }

  // Fail-safe: callers track from inside writes (a workspace create, an
  // upload), and a throw here must never fail the write it describes.
  try {
    const commonProps: Record<string, unknown> = {
      tenant,
      // Templatize secret-bearing routes (/login/<token> -> /login/:token) so a
      // verification/reset token never rides into analytics (AI-8118).
      page: sanitizePath(window.location.pathname),
    };
    const merged = { ...commonProps, ...properties };

    if (isLocalDev && !posthog.__loaded) {
      console.log(`[tracking] ${name}`, merged);
      return;
    }

    posthog.capture(name, merged);
  } catch (error) {
    // Tracking is best-effort: the event is dropped, loudly in local dev only.
    if (isLocalDev) console.warn(`[tracking] dropped ${name}`, error);
  }
};
